import React, { useState, createContext } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';
import ErpButton from './ErpButton';
import { ChevronDown } from 'lucide-react';

export const ErpDropdownContext = createContext<() => void>(() => {});

export interface ErpDropdownProps {
  id?: string;
  /** A composed trigger keeps app chrome on the ERP dropdown contract. */
  trigger?: React.ReactNode;
  contentClass?: string;
  ariaLabel?: string;
  variant?: string;
  size?: 'sm' | 'lg' | string;
  right?: boolean;
  noCaret?: boolean;
  toggleClass?: string;
  className?: string;
  buttonContent?: React.ReactNode;
  children?: React.ReactNode | ((props: { close: () => void }) => React.ReactNode);
}

const ErpDropdown: React.FC<ErpDropdownProps> = ({
  id,
  trigger,
  contentClass,
  ariaLabel,
  variant,
  size,
  right,
  noCaret,
  toggleClass = '',
  className = '',
  buttonContent,
  children,
}) => {
  const [open, setOpen] = useState(false);

  let mappedVariant: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" = "default";
  const v = variant ? variant.toLowerCase() : '';
  if (v.includes('danger')) mappedVariant = "destructive";
  else if (v.includes('outline')) mappedVariant = "outline";
  else if (v.includes('secondary') || v.includes('light')) mappedVariant = "secondary";
  else if (v.includes('transparent') || v.includes('ghost')) mappedVariant = "ghost";

  let mappedSize: "default" | "sm" | "lg" = "default";
  if (size === 'sm') mappedSize = "sm";
  if (size === 'lg') mappedSize = "lg";

  return (
    <ErpDropdownContext.Provider value={() => setOpen(false)}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {trigger || <ErpButton id={id} ariaLabel={ariaLabel} variant={mappedVariant === 'destructive' ? 'danger' : mappedVariant} size={mappedSize} className={`${toggleClass} ${className}`.trim()}>
            {buttonContent}
            {!noCaret && <ChevronDown className="ml-2 h-4 w-4" />}
          </ErpButton>}
        </DropdownMenuTrigger>
        <DropdownMenuContent align={right ? "end" : "start"} className={`w-56 min-w-[12rem] max-w-[calc(100vw-2rem)] ${contentClass || ''}`}>
          {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
        </DropdownMenuContent>
      </DropdownMenu>
    </ErpDropdownContext.Provider>
  );
};

export default ErpDropdown;
